import { getActiveOrganization, clearActiveOrganization } from '../utils/activeOrganization';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { fetchAuthSession, signIn, signOut, signUp, getCurrentUser, fetchUserAttributes, confirmSignIn, signInWithRedirect } from 'aws-amplify/auth';
import 'aws-amplify/auth/enable-oauth-listener';
import { Hub } from 'aws-amplify/utils';
import { User, UserRole } from '../types';
import { clearAllPrescriptionDrafts } from '../hooks/usePrescriptionDraft';
import { clearRecentPatients } from '../utils/recentPatients';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  /** Message from a failed Google sign-in (e.g. no DrEase account for that email) */
  ssoError: string;
  logout: () => Promise<void>;
  register: (email: string, password: string, firstName: string, lastName: string, role: UserRole) => Promise<void>;
  refreshUser: () => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  requiresNewPassword: boolean;
  tempUserEmail: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SSO_RETRY_KEY = 'sso-link-retry';
const LINKED_RETRY = 'LINKED_RETRY';

/** Turns Cognito's redirect error into something a clinic user can act on. */
const describeSsoError = (error: unknown): string => {
  const text = String((error as any)?.message ?? error ?? '').trim();
  if (/already found an entry/i.test(text)) return LINKED_RETRY;
  const preSignUp = text.match(/PreSignUp failed with error (.+?)\.?$/i);
  if (preSignUp) return preSignUp[1];
  return 'Google sign-in failed. Please try again or use your email and password.';
};

// Cognito reports a refused Google sign-in (e.g. no DrEase account for that email) as
// ?error_description=... on the redirect. Read it before Amplify rewrites the URL, because the
// Hub failure event isn't always delivered and the page then looks stuck.
const initialRedirectError = (() => {
  try {
    return new URLSearchParams(window.location.search).get('error_description');
  } catch {
    return null;
  }
})();

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [requiresNewPassword, setRequiresNewPassword] = useState(false);
  const [tempUserEmail, setTempUserEmail] = useState('');
  const [ssoError, setSsoError] = useState('');

  const loadUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      const session = await fetchAuthSession();
      
      const userRole = (attributes['custom:role'] as UserRole) || UserRole.PATIENT;

      const userData: User = {
        userId: attributes.sub || '',
        cognitoUserId: currentUser.userId,
        email: attributes.email || '',
        role: userRole,
        firstName: attributes.given_name || '',
        lastName: attributes.family_name || '',
        phoneNumber: attributes.phone_number,
        registrationNumber: attributes['custom:registration_number'],
        specialization: attributes['custom:specialization'],
        profilePhoto: attributes.picture,
        organizationId: attributes['custom:organizationId'] || attributes['custom:organization_id'],
      };

      // Hospital chosen with the switcher, or a superadmin's read-only support view
      const activeOrg = getActiveOrganization();
      if (activeOrg?.support && userRole === UserRole.SUPERADMIN) {
        userData.realRole = UserRole.SUPERADMIN;
        userData.role = UserRole.ADMIN;
        userData.supportMode = true;
        userData.organizationId = activeOrg.id;
        userData.organizationName = activeOrg.name;
      } else if (activeOrg?.support) {
        clearActiveOrganization();
      } else if (activeOrg?.id) {
        userData.organizationId = activeOrg.id;
        userData.organizationName = activeOrg.name;
      }

      setUser(userData);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();

    if (initialRedirectError) {
      const message = describeSsoError(initialRedirectError);
      if (message !== LINKED_RETRY) {
        sessionStorage.removeItem(SSO_RETRY_KEY);
        setSsoError(message);
      }
      window.history.replaceState(null, '', window.location.pathname);
    }

    // Completes "Sign in with Google" when Cognito redirects back to the app
    return Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        sessionStorage.removeItem(SSO_RETRY_KEY);
        setSsoError('');
        loadUser();
      } else if (payload.event === 'signInWithRedirect_failure') {
        const message = describeSsoError((payload as any).data?.error);
        // The very first Google sign-in links it to the existing account, which Cognito
        // reports as a failure; the immediate retry then succeeds.
        if (message === LINKED_RETRY && !sessionStorage.getItem(SSO_RETRY_KEY)) {
          sessionStorage.setItem(SSO_RETRY_KEY, '1');
          signInWithRedirect({ provider: 'Google' }).catch(() => undefined);
          return;
        }
        sessionStorage.removeItem(SSO_RETRY_KEY);
        setSsoError(message === LINKED_RETRY ? 'Google sign-in was linked to your account. Please try again.' : message);
        setLoading(false);
      }
    });
  }, []);

  const loginWithGoogle = async () => {
    setSsoError('');
    await signInWithRedirect({ provider: 'Google', options: { prompt: 'SELECT_ACCOUNT' } });
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const signInResult = await signIn({ username: email, password });
      
      // Check if password change is required
      if (signInResult.isSignedIn) {
        await loadUser();
        setRequiresNewPassword(false);
        setTempUserEmail('');
      } else if (signInResult.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setRequiresNewPassword(true);
        setTempUserEmail(email);
        setLoading(false);
        throw new Error('NEW_PASSWORD_REQUIRED');
      } else {
        await loadUser();
      }
    } catch (error: any) {
      setLoading(false);
      // If it's our custom NEW_PASSWORD_REQUIRED error, don't reset the state
      if (error.message !== 'NEW_PASSWORD_REQUIRED') {
        setRequiresNewPassword(false);
        setTempUserEmail('');
      }
      throw error;
    }
  };

  const completeNewPassword = async (newPassword: string) => {
    setLoading(true);
    try {
      await confirmSignIn({
        challengeResponse: newPassword,
      });
      await loadUser();
      setRequiresNewPassword(false);
      setTempUserEmail('');
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    const { resetPassword } = await import('aws-amplify/auth');
    await resetPassword({ username: email });
  };

  const resetPassword = async (email: string, code: string, newPassword: string) => {
    const { confirmResetPassword } = await import('aws-amplify/auth');
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  };

  const logout = async () => {
    try {
      await signOut();
      clearActiveOrganization();
      clearAllPrescriptionDrafts();
      clearRecentPatients();
      setUser(null);
      setRequiresNewPassword(false);
      setTempUserEmail('');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
    role: UserRole
  ) => {
    setLoading(true);
    try {
      await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName,
            // Role is never self-assigned: public sign-ups are patients; staff are invited by admins
          },
        },
      });
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const refreshUser = async () => {
    await loadUser();
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout,
      loginWithGoogle,
      ssoError, 
      register, 
      refreshUser,
      completeNewPassword,
      forgotPassword,
      resetPassword,
      requiresNewPassword,
      tempUserEmail
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
